import React, { useEffect, useRef, useState } from 'react';
import { Header, Title } from '../ShelfPage/styles';
import { GenericNavigationTabs } from 'src/components/BeaconV2Components/SubtabNav/SubtabNavigation';
import axios from 'axios';
import { buildLlmCrawlerPath } from 'src/utils/app';
import { useAppSelector } from 'src/utils/Hooks/typedHooks';
import { useHistory, useLocation } from 'src/utils/Hooks';
import { useUpdateQueryParams } from 'src/utils/Hooks/useUpdateQueryParams';
import _get from 'lodash/get';
import SummaryCampaignDetails from './SummaryCampaignDetails';
import ShelfCreditBalanceCard from '../components/ShelfCreditBalanceCard';
import ConsumptionHeader from '../components/ConsumptionHeader';
import { DateRangeValue, isTimestampWithinRange } from '../shared';
import CrawlCampaignsEntityTable from '../components/CrawlCampaignsEntityTable';
import { useUserEmail } from 'src/utils/Hooks/reduxSelectors';
import DateRangeDropdown from 'src/components/Shelf/components/DateRangeDropdown';

const SummaryTab: React.FC<{ onNavigateToDataTab?: (batchId?: string) => void }> = ({ onNavigateToDataTab }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [crawlCampaigns, setCrawlCampaigns] = useState<any[]>([]);
  const [isLoadingCampaigns, setIsLoadingCampaigns] = useState<boolean>(false);
  const [selectedCampaign, setSelectedCampaign] = useState<any | null>(null);
  const [creditsUsed, setCreditsUsed] = useState<number | null>(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState<boolean>(false);
  const [averageSuccessRate, setAverageSuccessRate] = useState<string | null>(null);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [lastRunByCampaignId, setLastRunByCampaignId] = useState<Record<string, string>>({});
  const [lastRunTsByCampaignId, setLastRunTsByCampaignId] = useState<Record<string, number>>({});
  const [batchCountByCampaignId, setBatchCountByCampaignId] = useState<Record<string, number>>({});
  const [range, setRange] = useState<DateRangeValue>('all');
  const userEmail = useUserEmail() || '';
  const [agentTab, setAgentTab] = useState<'mine' | 'all'>('all');
  const divisionId = useAppSelector((state) => _get(state, ['user', 'session', 'divisionId'], null));
  const history = useHistory();
  const location = useLocation();
  const updateQuery = useUpdateQueryParams(history);


  const normalizeCampaignsResponse = (data: any) => {
    if (Array.isArray(data?.campaigns)) return data.campaigns;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data)) return data;
    return [] as any[];
  };
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const isActiveCampaign = (c: any) => {
    const statusRaw = (c?.status || '').toString().toLowerCase();
    return !['disabled', 'inactive'].includes(statusRaw);
  };

  useEffect(() => {
    const exitDetails = () => {
      try {
        const params = new URLSearchParams(location.search);
        params.delete('shelfCampaignId');
        history.push(`${location.pathname}?${params.toString()}`);
      } catch {}
      setSelectedCampaign(null);
    };
    window.addEventListener('shelf:go-summary' as any, exitDetails);
    return () => window.removeEventListener('shelf:go-summary' as any, exitDetails);
  }, [history, location.search, location.pathname]);

  const fetchCrawlCampaigns = React.useCallback(async () => {
    let active = true;
    try {
      setIsLoadingCampaigns(true);
      const path = buildLlmCrawlerPath('crawl-campaigns?env=dev');
      const params = divisionId ? { divisionId } : undefined;
      const { data } = await axios.get(path, { params });
      const list = normalizeCampaignsResponse(data);
      if (active) setCrawlCampaigns(list);
    } catch (e) {
      console.error('Failed to fetch crawl campaigns', e);
      if (active) setCrawlCampaigns([]);
    } finally {
      if (active) setIsLoadingCampaigns(false);
    }
    return () => {
      active = false;
    };
  }, [divisionId]);

  useEffect(() => {
    const cancel = fetchCrawlCampaigns();
    return () => { try { (cancel as any)?.(); } catch {} };
  }, [fetchCrawlCampaigns]);

  useEffect(() => {
    let cancelled = false;
    const getId = (c: any) => c?.id || c?.campaignId || c?.crawlCampaignId || c?.uuid || c?.campaign_id || c?.crawl_campaign_id;
    const getStartTs = (b: any) => {
      const ts = b?.startedAt || b?.startTime || b?.createdAt || null;
      return ts ? new Date(ts).getTime() : -Infinity;
    };
    const fetchLatest = async (id: string | number) => {
      const params: any = { env: 'dev' };
      if (divisionId) params.divisionId = divisionId;
      const path = buildLlmCrawlerPath(`crawl-campaigns/${id}/batches`);
      const { data } = await axios.get(path, { params });
      const list: any[] = Array.isArray(data?.batches) ? data.batches : Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      if (!list.length) return { dateStr: '—', ts: -Infinity, count: 0 } as const;
      const latest = list.reduce((acc, cur) => (getStartTs(cur) > getStartTs(acc) ? cur : acc), list[0]);
      const ts = latest?.startedAt || latest?.startTime || latest?.createdAt;
      return ts
        ? { dateStr: new Date(ts).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }), ts: new Date(ts).getTime(), count: list.length }
        : { dateStr: '—', ts: -Infinity, count: list.length };
    };
    (async () => {
      try {
        const entries = await Promise.all(
          crawlCampaigns
            .map((c) => [String(getId(c) ?? ''), c] as const)
            .filter(([id]) => !!id)
            .map(async ([id]) => [id, await fetchLatest(id)])
        );
        if (!cancelled) {
          const map: Record<string, string> = {};
          const tsMap: Record<string, number> = {};
          const countMap: Record<string, number> = {};
          entries.forEach(([id, payload]) => {
            const res = payload as { dateStr: string; ts: number; count: number };
            map[id as string] = res.dateStr;
            tsMap[id as string] = res.ts;
            countMap[id as string] = res.count;
          });
          setLastRunByCampaignId(map);
          setLastRunTsByCampaignId(tsMap);
          setBatchCountByCampaignId(countMap);
        }
      } catch (e) {
        if (!cancelled) {
          setLastRunByCampaignId({});
          setLastRunTsByCampaignId({});
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [crawlCampaigns, divisionId]);

  const filteredCampaigns = React.useMemo(() => {
    if (!crawlCampaigns || !crawlCampaigns.length) return crawlCampaigns;
    const now = Date.now();
    return crawlCampaigns.filter((c: any) => {
      const id = c?.id || c?.campaignId || c?.crawlCampaignId || c?.uuid || c?.campaign_id || c?.crawl_campaign_id;
      const ts = lastRunTsByCampaignId[String(id)] ?? -Infinity;
      return isTimestampWithinRange(ts, range, now);
    });
  }, [crawlCampaigns, lastRunTsByCampaignId, range]);

  const displayedCampaigns = React.useMemo(() => {
    if (agentTab !== 'mine') return filteredCampaigns;
    return (filteredCampaigns || []).filter((c: any) => {
      const createdBy = (c?.createdBy || '').toString();
      if (!createdBy) return false;
      return createdBy.toLowerCase() === userEmail.toLowerCase();
    });
  }, [agentTab, filteredCampaigns, userEmail]);

  const totalAgentsCount = React.useMemo(() => (displayedCampaigns || []).length, [displayedCampaigns]);
  const activeAgentsCount = React.useMemo(() => (displayedCampaigns || []).filter(isActiveCampaign).length, [displayedCampaigns]);

  // Restore selection from URL on load or when campaigns list updates
  useEffect(() => {
    try {
      const params = new URLSearchParams(location.search);
      const urlCampaignId = params.get('shelfCampaignId');
      if (!urlCampaignId) {
        return;
      }

      const getId = (c: any) => c?.id || c?.campaignId || c?.crawlCampaignId || c?.uuid || c?.campaign_id || c?.crawl_campaign_id;
      const currentId = selectedCampaign ? String(getId(selectedCampaign)) : null;
      if (currentId === urlCampaignId) {
        return;
      }

      const match = (crawlCampaigns || []).find((c) => String(getId(c)) === String(urlCampaignId));
      if (match) {
        setSelectedCampaign(match);
      }
    } catch {}
  }, [location.search, crawlCampaigns, selectedCampaign]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setIsLoadingCredits(true);
        const now = Date.now();
        const params: any = { env: 'dev' };
        if (divisionId) params.divisionId = divisionId;

        const getId = (c: any) => c?.id || c?.campaignId || c?.crawlCampaignId || c?.uuid || c?.campaign_id || c?.crawl_campaign_id;
        const getStartTs = (b: any) => {
          const ts = b?.startedAt || b?.startTime || b?.createdAt || null;
          return ts ? new Date(ts).getTime() : -Infinity;
        };
        const toNum = (v: any) => {
          if (typeof v === 'number') return v;
          if (typeof v === 'string') {
            const n = Number(v.replace(/,/g, ''));
            return Number.isNaN(n) ? 0 : n;
          }
          return 0;
        };
        const toSuccessPercent = (val: any): number | null => {
          if (val == null) return null;
          const pct: number = typeof val === 'number' ? (val > 1 ? val : val * 100) : Number(val);
          if (Number.isNaN(pct)) return null;
          if (pct <= 0) return null;
          return pct;
        };

        const ids = (displayedCampaigns || [])
          .map((c: any) => String(getId(c) ?? ''))
          .filter((id: string) => !!id);

        const perCampaignMetrics = await Promise.all(
          ids.map(async (id: string) => {
            try {
              const path = buildLlmCrawlerPath(`crawl-campaigns/${id}/batches`);
              const { data } = await axios.get(path, { params });
              const list: any[] = Array.isArray(data?.batches)
                ? data.batches
                : Array.isArray(data?.items)
                ? data.items
                : Array.isArray(data)
                ? data
                : [];
              const filtered = list.filter((b) => isTimestampWithinRange(getStartTs(b), range, now));
              const costSum = filtered.reduce((sum, b) => sum + toNum(b?.actualCost ?? b?.actual_cost ?? 0), 0);
              const successAgg = filtered.reduce(
                (acc: { sum: number; count: number }, b: any) => {
                  const val = toSuccessPercent(b?.successRate ?? b?.successPercent ?? null);
                  if (val != null) {
                    acc.sum += val;
                    acc.count += 1;
                  }
                  return acc;
                },
                { sum: 0, count: 0 }
              );
              return { costSum, successSum: successAgg.sum, successCount: successAgg.count };
            } catch (e) {
              return { costSum: 0, successSum: 0, successCount: 0 };
            }
          })
        );

        const totalCost = perCampaignMetrics.reduce((a, b) => a + b.costSum, 0);
        const totalSuccessSum = perCampaignMetrics.reduce((a, b) => a + b.successSum, 0);
        const totalSuccessCount = perCampaignMetrics.reduce((a, b) => a + b.successCount, 0);
        if (!cancelled) {
          setCreditsUsed(totalCost);
          if (totalSuccessCount > 0) {
            const avg = totalSuccessSum / totalSuccessCount;
            const rounded = Math.round(avg);
            setAverageSuccessRate(`${rounded}%`);
          } else {
            setAverageSuccessRate(null);
          }
        }
      } catch (e) {
        if (!cancelled) {
          setCreditsUsed(null);
          setAverageSuccessRate(null);
        }
        // eslint-disable-next-line no-console
        console.error('Failed to compute credits used from batches', e);
      } finally {
        if (!cancelled) setIsLoadingCredits(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [displayedCampaigns, range, divisionId]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const params: any = { env: 'dev' };
        if (divisionId) params.divisionId = divisionId;
        const { data } = await axios.get(buildLlmCrawlerPath('credits'), { params });
        const toNum = (v: any) => {
          if (typeof v === 'number') return v;
          if (typeof v === 'string') {
            const n = Number(v.replace(/,/g, ''));
            return Number.isNaN(n) ? null : n;
          }
          return null;
        };
        const balance = toNum((data || {}).creditBalance) ?? toNum((data || {}).balance) ?? toNum((data || {}).remaining) ?? null;
        if (mounted) setCreditBalance(balance);
      } catch (e) {
        if (mounted) setCreditBalance(null);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [divisionId]);

  if (selectedCampaign) {
    return (
        <SummaryCampaignDetails
        campaign={selectedCampaign}
        campaignId={(selectedCampaign.id || selectedCampaign.campaignId || selectedCampaign.crawlCampaignId || selectedCampaign.uuid) as any}
          onBack={() => setSelectedCampaign(null)}
          onSaved={async () => {
            await fetchCrawlCampaigns();
          }}
      />
    );
  }

  return (
    <>
      <Header style={{ marginBottom: 0 }}>
        <Title>Agent Summary</Title>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => updateQuery({ additionalParams: { subtab: 'templates' } })}
              style={{
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid #E6ECF2',
                background: '#031425',
                color: 'white',
                fontWeight: 500,
                cursor: 'pointer',
                fontSize: 14,
                width: 120,
                fontFamily: 'Roboto'
              }}
            >
              + Add Agent
            </button>
            <DateRangeDropdown value={range} onChange={setRange} />
          </div>
          <div />
        </div>
        {/* <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 12 }}>
          <ShelfCreditBalanceCard />
        </div> */}
      </Header>

      <div style={{ width: 1045, paddingTop: 0 }}>
        <GenericNavigationTabs
          tab={'keyMetrics'}
          tabs={[
            {
              label: 'Key Metrics',
              onClick: () => { },
              value: 'keyMetrics'
            }
          ]}
        />

      </div>
      <div>
        <ConsumptionHeader totalCrawls={totalAgentsCount} creditsUsed={creditsUsed} successRate={averageSuccessRate} activeCrawls={activeAgentsCount} />
      </div>
      <div style={{ width: 1045 }}>
        <div style={{ fontWeight: 700, fontSize: '20px', marginTop: '32px' }}>Active Agents</div>
        <div style={{ margin: '8px 0 24px 0' }}>
          <GenericNavigationTabs
            tab={agentTab}
            tabs={[
              { label: 'All Agents', value: 'all', onClick: () => setAgentTab('all') },
              { label: 'My Agents', value: 'mine', onClick: () => setAgentTab('mine') }
            ]}
          />
        </div>
          <CrawlCampaignsEntityTable
            campaigns={displayedCampaigns}
            isLoading={isLoadingCampaigns}
            lastRunByCampaignId={lastRunByCampaignId}
            lastRunTsByCampaignId={lastRunTsByCampaignId}
            batchCountByCampaignId={batchCountByCampaignId}
            onSelectCampaign={(c) => {
              setSelectedCampaign(c);
              const id = c?.id || c?.campaignId || c?.crawlCampaignId || c?.uuid || c?.campaign_id || c?.crawl_campaign_id;
              if (id) {
                updateQuery({ additionalParams: { shelfCampaignId: String(id) } });
              }
            }}
          />

      </div>
    </>
  );
};

export default SummaryTab;


